import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Trophy, MessageCircle, ArrowLeft } from "lucide-react";
import FunnelLayout from "@/components/app/FunnelLayout";
import ExitIntentModal from "@/components/app/ExitIntentModal";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { track } from "@/lib/analytics";
import { TESTIMONIAL_AVATARS, COMMUNITY_MOCKUP } from "@/lib/assets";

const TESTIMONIALS = [
  {
    name: "עומר מ.",
    age: 38,
    role: "מהנדס תוכנה, נשוי + 3",
    avatar: TESTIMONIAL_AVATARS.omer,
    title: "הייתי הלארג' של החבר'ה – ובפנים רעדתי",
    quote:
      'הרווחנו יחד כמעט 30 נטו, אבל כל חודש נגמר במינוס של 3,000 ש"ח. הפסקנו לטוס עם הילדים, אבל בחוץ שיחקתי אותה שהכל בסדר. התביישתי לשתף.',
    resultLabel: "התוצאה לאחר איחוד הלוואות:",
    result:
      'חיסכון של 4,800 ש"ח בהחזר החודשי! בשבוע שעבר חזרנו ממשפחתית מלאה בברצלונה – ששולמה מראש במזומן מהקופה שפתחנו, בלי שקל אחד של הלוואה.',
  },
  {
    name: "רונה ש.",
    age: 42,
    role: "מנהלת שיווק, נשואה + 2",
    avatar: TESTIMONIAL_AVATARS.rona,
    title: "שברתי לילדים את קופת החיסכון",
    quote:
      "כדי לסגור את המינוס שתפח בגלל הלוואות הגישור, נאלצנו למשוך את כספי החיסכון ששמנו ללימודים של הילדים. זה היה רגע של שפל ורגשות אשם איומים.",
    resultLabel: "התוצאה לאחר יישום השיטה:",
    result:
      'בנינו תזרים מחדש סביב המשכנתה על הנכס שלנו. עצרנו את גלגול החובות. היום יש לנו "הוראת קבע לעצמנו" – 1,500 ש"ח בכל חודש מופרשים אוטומטית לעתיד של הילדים, עוד לפני שהכסף נכנס לעו"ש. הלילות שקטים.',
  },
];

const CHECKLIST = [
  {
    id: "map",
    num: 1,
    title: 'ביצעתי "מיפוי חוסם" מלא',
    sub: "עברתי על כל הלוואות הבנק, האשראי, המינוס וההלוואות החוץ-בנקאיות (כפי שלמדנו במדריך 2). אני יודע בדיוק כמה אני מחזיר בחודש.",
  },
  {
    id: "couple",
    num: 2,
    title: 'שמתי סוף ל"בושה הזוגית"',
    sub: "ישבתי עם בן/בת הזוג, שמנו את כל המספרים מהמיפוי על השולחן, והסכמנו להפסיק להאשים ולהתחיל לעבוד כצוות.",
  },
  {
    id: "eligibility",
    num: 3,
    title: "בדקתי זכאות לאיחוד הלוואות על בסיס הנכס",
    sub: "מילאתי את שאלון האבחון כדי לדעת אם הדירה שלי יכולה להציל אותי מהריביות ולספק לי חמצן מיידי.",
  },
  {
    id: "envelopes",
    num: 4,
    title: 'תכננתי את "שיטת המעטפות" שלי',
    sub: 'החלטתי איך לחלק את הכסף שיתפנה (החמצן) בין: מותרות וחופשות, קופת בלת"מים, וחיסכון לעתיד (כפי שתירגלנו במדריך 3).',
  },
  {
    id: "community",
    num: 5,
    title: 'הצטרפתי לקהילת הווצאפ של "אמת או חובות"',
    sub: "אני לא עובר את זה יותר לבד. נכנסתי לקהילה כדי לשאוב השראה, לשאול שאלות ולקבל כלים על בסיס שבועי.",
  },
];

export default function FunnelGuide4() {
  const [checked, setChecked] = useState({});
  const completed = useMemo(
    () => CHECKLIST.filter((c) => checked[c.id]).length,
    [checked]
  );
  const pct = (completed / CHECKLIST.length) * 100;
  const allDone = completed === CHECKLIST.length;

  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));

  return (
    <FunnelLayout
      step={4}
      title="מהישרדות לצמיחה"
      loadingMessages={["מכין את התוכנית הסופית...", "טוען עדויות מקהילה..."]}
    >
      {(lead) => (
        <>
          {/* Section 1: Testimonials intro */}
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-[800] mb-3">אל תאמינו לנו, תאמינו להם</h2>
            <p className="text-stone-600 leading-[1.7] max-w-2xl mx-auto">
              מאות משפחות כמוכם, שחיות את אותה המציאות בישראל, כבר עשו את הצעד הזה. הנה מה שקורה כשמחליטים לשבור את הכלים מול הבנק.
            </p>
          </div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-2 gap-5 mb-10">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white rounded-3xl p-6 border border-black/5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <img src={t.avatar} alt={t.name} className="w-14 h-14 rounded-full object-cover" />
                  <div>
                    <div className="font-[800]">{t.name} ({t.age})</div>
                    <div className="text-sm text-stone-500">{t.role}</div>
                  </div>
                </div>
                <h4 className="text-lg font-[800] mb-3 leading-snug">"{t.title}"</h4>
                <p className="text-sm text-stone-600 leading-relaxed mb-4">"{t.quote}"</p>
                <div className="border-t border-stone-100 pt-3">
                  <p className="text-sm text-stone-700 leading-relaxed">
                    <strong className="text-emerald-600">{t.resultLabel}</strong> {t.result}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Checklist intro */}
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-[800] mb-3">הצ'קליסט להמראה: המשימות שלכם</h2>
            <p className="text-stone-600 leading-[1.7] max-w-2xl mx-auto">
              אל תנסו לפתור הכל ביום אחד. הנה 5 הצעדים הפרקטיים שאתם צריכים לבצע בשבועות הקרובים כדי לייצב את הספינה. סמנו 'וי' על מה שכבר עשיתם.
            </p>
          </div>

          {/* Progress + checklist */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-black/5 mb-8">
            <div className="flex items-center justify-between mb-3">
              <div className="font-[800]">התקדמות התוכנית:</div>
              <div className="text-2xl font-[800] text-brand-amber" data-testid="checklist-percent">
                {Math.round(pct)}%
              </div>
            </div>
            <Progress value={pct} className="h-3 mb-6" data-testid="checklist-progress" />

            <div className="space-y-3" data-testid="checklist">
              {CHECKLIST.map((item) => {
                const isChecked = !!checked[item.id];
                return (
                  <button
                    key={item.id}
                    onClick={() => toggle(item.id)}
                    data-testid={`checklist-${item.id}`}
                    className={`w-full text-right p-4 rounded-2xl border-2 transition-all flex items-start gap-4 ${
                      isChecked ? "border-emerald-400 bg-emerald-50" : "border-stone-200 hover:border-stone-300 bg-white"
                    }`}
                  >
                    <div
                      className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
                        isChecked ? "bg-brand-amber border-brand-amber" : "border-2 border-stone-300 bg-white"
                      }`}
                    >
                      {isChecked && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold mb-1">
                        <span className="text-brand-amber me-1">{item.num}.</span> {item.title}
                      </div>
                      <div className="text-sm text-stone-600 leading-[1.6]">{item.sub}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <AnimatePresence>
              {allDone && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="mt-6 bg-gradient-to-tr from-amber-50 to-emerald-50 rounded-2xl p-6 text-center border border-amber-200"
                  data-testid="checklist-complete"
                >
                  <div className="text-4xl mb-2">🎉</div>
                  <h4 className="text-xl font-[800] mb-2">אלופים! סיימתם את התוכנית!</h4>
                  <p className="text-sm text-stone-700 leading-[1.6] max-w-md mx-auto">
                    לקחתם אחריות מלאה על העתיד הכלכלי שלכם. הצעד הבא הוא פשוט ללחוץ על הכפתורים למטה כדי להניע את גלגלי השינוי באופן מעשי.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Final CTA banner */}
          <div className="bg-gradient-to-tr from-brand-amber to-brand-amberHover text-white rounded-3xl p-6 md:p-10 mb-10 relative overflow-hidden">
            <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
            <div className="relative grid md:grid-cols-5 gap-6 items-center">
              <div className="md:col-span-3">
                <h3 className="text-2xl md:text-3xl font-[800] mb-3 leading-tight">הזמן לשנות הוא עכשיו.</h3>
                <p className="text-white/90 leading-[1.7] mb-6">
                  זה סוף המדריך, אבל זו רק תחילת הדרך שלכם למציאות חדשה. אתם במרחק של טופס אחד ויחיד מאוויר לנשימה ומאלפי שקלים שחוזרים אליכם בכל חודש.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={lead.affiliate_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="affiliate-cta-btn"
                    onClick={() =>
                      track("funnel_affiliate_click", {
                        campaignId: lead.campaign_id,
                        leadId: lead.lead_id,
                        payload: { source: "guide4_primary" },
                      })
                    }
                    className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-full bg-white text-brand-amber font-[800] hover:bg-stone-50 transition active:scale-95 pulse-cta"
                  >
                    בדקו זכאות לאיחוד הלוואות
                    <ArrowLeft className="w-5 h-5" />
                  </a>
                  <a
                    href={lead.whatsapp_url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="whatsapp-cta-btn"
                    onClick={() =>
                      track("funnel_affiliate_click", {
                        campaignId: lead.campaign_id,
                        leadId: lead.lead_id,
                        payload: { source: "guide4_whatsapp" },
                      })
                    }
                    className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-full bg-white/15 backdrop-blur text-white border border-white/30 font-bold hover:bg-white/25 transition active:scale-95"
                  >
                    <MessageCircle className="w-5 h-5" />
                    הצטרפו לקהילת הווצאפ
                  </a>
                </div>
                <p className="text-xs text-white/80 mt-4">* השאלון הינו דיסקרטי לחלוטין ולוקח פחות מ-2 דקות.</p>
              </div>
              <div className="md:col-span-2">
                <img src={COMMUNITY_MOCKUP} alt="קהילה" className="w-full h-auto rounded-2xl shadow-2xl" />
              </div>
            </div>
          </div>

          {/* Exit-Intent Modal */}
          <ExitIntentModal lead={lead} source="guide4" />
        </>
      )}
    </FunnelLayout>
  );
}
